import type { TooltipProps } from "@mui/material";
import { Box, Tooltip, Typography, styled, tooltipClasses } from "@mui/material";
import type { FC } from "react";
import { Link } from "react-router";

type EmailTemplateEditorTabProps = {
  to: string;
  label: string;
  selected: boolean;
  disabled: boolean;
  disabledTooltipText: string | undefined;
};

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
  },
}));

export const EMailTemplateEditorTab: FC<EmailTemplateEditorTabProps> = ({
  to,
  label,
  selected,
  disabled,
  disabledTooltipText,
}) => {
  const textElement = (
    <Typography
      variant="body2"
      sx={(theme) => ({
        textDecoration: "none",
        ":hover": {
          textDecoration: disabled ? "none" : "underline",
          color: theme.palette.primary.main,
        },
        color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
        fontWeight: "500",
      })}
    >
      {label}
    </Typography>
  );

  return (
    <Box
      role="tab"
      {...(!disabled ? { component: Link, to } : {})}
      sx={(theme) => ({
        border: `1px solid transparent`,
        borderRight: "none",
        position: "relative",
        ...(selected && {
          border: `1px solid ${theme.palette.divider}`,
          ":after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: -1,
            bottom: 0,
            width: "1px",
            backgroundColor: theme.palette.background.paper,
          },
        }),
        py: 1,
        px: 2,
        borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
        cursor: "pointer",
        backgroundColor: selected ? theme.palette.background.paper : "transparent",
        ...(disabled && {
          opacity: 0.5,
          cursor: "not-allowed",
        }),
        textDecoration: "none",
      })}
    >
      {disabled && disabledTooltipText ? (
        <StyledTooltip
          title={disabledTooltipText}
          followCursor
          placement="top"
          sx={{
            opacity: 0.8,
          }}
        >
          {textElement}
        </StyledTooltip>
      ) : (
        textElement
      )}
    </Box>
  );
};
